import {
  Color3,
  Mesh,
  MeshBuilder,
  Scene,
  StandardMaterial,
  TransformNode,
  Vector3,
} from "@babylonjs/core";
import type { MallStats } from "@mall-mart/shared";
import { DynamicTextureSurface } from "./surfaces/DynamicTextureSurface.js";
import type { SurfaceContent, SurfaceTheme } from "./surfaces/types.js";
import { resizeShopifyImage } from "./imageUtils.js";
import { getSharedMaterials } from "./materials.js";
import { relativeSoldTime } from "../util/relativeTime.js";

const SLIDE_INTERVAL_MS: number = 8_000;
const THERMOMETER_FILL_HEIGHT: number = 4.6;

// The jumbotron is a screen and stays dark; wall-mounted boards (sell goal,
// giving wall) are light minimalist panels matching the wayfinding signage.
const DISPLAY_THEME: SurfaceTheme = {
  background: "#15181c",
  foreground: "#f4f6f8",
  accent: "#6fb3e0",
  fontFamily: "sans-serif",
  fontFamilySerif: "serif",
};

const PANEL_THEME: SurfaceTheme = {
  background: "#f6f7f9",
  foreground: "#1a1d21",
  accent: "#5b6673",
  fontFamily: "sans-serif",
  fontFamilySerif: "serif",
};

/**
 * Live mall-wide commerce displays in the atrium, fed by /api/mall-stats:
 * - Jumbotron over the fountain cycling last-sold / sell-goal / most-bought.
 * - Fundraiser-style sell-goal thermometer beside the escalators.
 * - Giving wall (donations + charities) across the atrium.
 * All content is optional: sections missing from the stats simply don't get
 * a slide / stay at their placeholder, so a stats outage never breaks the mall.
 */
export class MallStatsDisplays {
  private readonly scene: Scene;
  private readonly root: TransformNode;
  private stats: MallStats | null = null;
  private readonly jumbotronFaces: DynamicTextureSurface[] = [];
  private slideIndex: number = 0;
  private thermometerFill: Mesh | undefined;
  private readonly thermometerLabels: DynamicTextureSurface[] = [];
  private givingWall: DynamicTextureSurface | undefined;

  constructor(scene: Scene, root: TransformNode) {
    this.scene = scene;
    this.root = root;
    this.buildJumbotron();
    this.buildThermometer();
    this.buildGivingWall();
    setInterval(() => this.advanceSlide(), SLIDE_INTERVAL_MS);
  }

  updateStats(stats: MallStats): void {
    this.stats = stats;
    this.renderSlide();
    this.renderThermometer();
    this.renderGivingWall();
  }

  // -- Jumbotron ----------------------------------------------------------

  private buildJumbotron(): void {
    // A hanging two-faced screen above the fountain, readable from the spawn
    // side (south face) and from the north promenade. The MALL MART ceiling
    // banner spans y 11.5..14.1 at the same spot; hang the jumbotron below it
    // (top at y ~11.2) so the two never interpenetrate.
    const frame: Mesh = MeshBuilder.CreateBox(
      "jumbotron-frame",
      { width: 9.4, height: 5.2, depth: 0.5 },
      this.scene
    );
    frame.position = new Vector3(0, 8.6, 0);
    const frameMat: StandardMaterial = new StandardMaterial("jumbotron-frame-mat", this.scene);
    frameMat.diffuseColor = new Color3(0.08, 0.09, 0.1);
    frame.material = frameMat;
    frame.parent = this.root;

    for (const facing of [0, Math.PI]) {
      const face: DynamicTextureSurface = new DynamicTextureSurface({
        scene: this.scene,
        name: `jumbotron-face-${facing === 0 ? "n" : "s"}`,
        widthM: 9,
        heightM: 4.8,
        resolution: { width: 1024, height: 546 },
        theme: DISPLAY_THEME,
        emissive: true,
        layout: "imageLeft",
        singleSided: true,
        excludeFromGlow: true,
      });
      // Offset each face just past the frame's depth on its side.
      const zOffset: number = facing === 0 ? 0.28 : -0.28;
      face.placeAt({
        position: new Vector3(0, 8.6, zOffset),
        rotationY: facing,
        parent: this.root,
      });
      face.setContent({ title: "MALL MART LIVE", bodyLines: ["Loading mall stats..."] });
      this.jumbotronFaces.push(face);
    }
  }

  private slides(): SurfaceContent[] {
    const s: MallStats | null = this.stats;
    if (s === null) {
      return [];
    }
    const slides: SurfaceContent[] = [];
    if (s.lastSold !== null) {
      const soldAgo: string | null = relativeSoldTime(s.lastSold.soldAt);
      slides.push({
        title: "JUST SOLD",
        subtitle: s.lastSold.title,
        imageUrl: resizeShopifyImage(s.lastSold.imageUrl, 512),
        bodyLines: soldAgo !== null ? [`Bought ${soldAgo}`] : [],
        ctaText: "SHOP THE MALL",
      });
    }
    if (s.sellGoal !== null) {
      slides.push({
        title: "MALL SELL GOAL",
        bodyLines: [
          `${s.sellGoal.itemsSold} / ${s.sellGoal.itemsNeeded} items sold`,
          `${s.sellGoal.itemsRemaining} to go (${s.sellGoal.progressPercent.toFixed(1)}%)`,
        ],
        ctaText: "EVERY SALE COUNTS",
      });
    }
    if (s.mostBought !== null) {
      const lines: string[] =
        s.mostBought.unitsSold !== undefined ? [`${s.mostBought.unitsSold} sold`] : [];
      slides.push({
        title: "MOST BOUGHT",
        subtitle: s.mostBought.title,
        imageUrl: resizeShopifyImage(s.mostBought.imageUrl, 512),
        bodyLines: lines,
        ctaText: "CROWD FAVORITE",
      });
    }
    return slides;
  }

  private advanceSlide(): void {
    this.slideIndex++;
    this.renderSlide();
  }

  private renderSlide(): void {
    const slides: SurfaceContent[] = this.slides();
    if (slides.length === 0) {
      return;
    }
    const content: SurfaceContent = slides[this.slideIndex % slides.length];
    for (const face of this.jumbotronFaces) {
      face.setContent(content);
    }
  }

  // -- Sell-goal thermometer ----------------------------------------------

  private buildThermometer(): void {
    // Beside the up escalator, visible on the walk from spawn to the wings.
    const base: Vector3 = new Vector3(8.5, 0, 15);

    const column: Mesh = MeshBuilder.CreateBox(
      "sell-goal-column",
      { width: 0.9, height: THERMOMETER_FILL_HEIGHT + 0.4, depth: 0.9 },
      this.scene
    );
    column.position = new Vector3(base.x, (THERMOMETER_FILL_HEIGHT + 0.4) / 2, base.z);
    const columnMat: StandardMaterial = new StandardMaterial("sell-goal-column-mat", this.scene);
    columnMat.diffuseColor = new Color3(0.09, 0.1, 0.12);
    columnMat.alpha = 0.55;
    column.material = columnMat;
    column.parent = this.root;

    const fill: Mesh = MeshBuilder.CreateBox(
      "sell-goal-fill",
      { width: 0.7, height: 1, depth: 0.7 },
      this.scene
    );
    const fillMat: StandardMaterial = new StandardMaterial("sell-goal-fill-mat", this.scene);
    fillMat.diffuseColor = new Color3(0.1, 0.5, 0.42);
    fillMat.emissiveColor = new Color3(0.15, 0.72, 0.6);
    fill.material = fillMat;
    fill.parent = this.root;
    fill.scaling.y = 0.001; // grows when stats arrive
    fill.position = new Vector3(base.x, 0.2, base.z);
    this.thermometerFill = fill;

    // Readable signs on both sides (south toward spawn/fountain, north
    // toward the promenade) and at both the top and the bottom of the
    // column. The default layout paints the title much larger than "list".
    const placements: Array<{ suffix: string; y: number; z: number; rotY: number }> = [
      { suffix: "top-s", y: THERMOMETER_FILL_HEIGHT + 1.5, z: base.z - 0.06, rotY: Math.PI },
      { suffix: "top-n", y: THERMOMETER_FILL_HEIGHT + 1.5, z: base.z + 0.06, rotY: 0 },
      { suffix: "bottom-s", y: 1.2, z: base.z - 0.55, rotY: Math.PI },
      { suffix: "bottom-n", y: 1.2, z: base.z + 0.55, rotY: 0 },
    ];
    for (const p of placements) {
      const label: DynamicTextureSurface = new DynamicTextureSurface({
        scene: this.scene,
        name: `sell-goal-label-${p.suffix}`,
        widthM: 3.8,
        heightM: 1.8,
        resolution: { width: 768, height: 364 },
        theme: PANEL_THEME,
        emissive: true,
        singleSided: true,
        excludeFromGlow: true,
      });
      label.placeAt({
        position: new Vector3(base.x, p.y, p.z),
        rotationY: p.rotY,
        parent: this.root,
      });
      label.setContent({ title: "SELL GOAL" });
      this.thermometerLabels.push(label);
    }
  }

  private renderThermometer(): void {
    const goal = this.stats?.sellGoal;
    if (goal === undefined || goal === null || this.thermometerFill === undefined) {
      return;
    }
    const pct: number = Math.min(Math.max(goal.progressPercent / 100, 0), 1);
    const height: number = Math.max(THERMOMETER_FILL_HEIGHT * pct, 0.05);
    this.thermometerFill.scaling.y = height;
    this.thermometerFill.position.y = 0.2 + height / 2;
    for (const label of this.thermometerLabels) {
      label.setContent({
        title: "SELL GOAL",
        bodyLines: [
          `${goal.itemsSold} / ${goal.itemsNeeded} sold`,
          `${goal.progressPercent.toFixed(1)}% there`,
        ],
      });
    }
  }

  // -- Giving wall ---------------------------------------------------------

  private buildGivingWall(): void {
    const wall: DynamicTextureSurface = new DynamicTextureSurface({
      scene: this.scene,
      name: "giving-wall",
      widthM: 4.2,
      heightM: 3,
      resolution: { width: 768, height: 548 },
      theme: PANEL_THEME,
      emissive: true,
      layout: "list",
      excludeFromGlow: true,
      singleSided: true,
    });
    // Southwest of the fountain, angled to face the atrium center.
    const pos: Vector3 = new Vector3(-11, 2.1, -11);
    // White backing slab so the single-sided panel reads as a freestanding
    // wall from behind instead of vanishing (or mirroring) there.
    const yaw: number = Math.atan2(-pos.x, -pos.z);
    const backing: Mesh = MeshBuilder.CreateBox(
      "giving-wall-backing", { width: 4.5, height: 3.3, depth: 0.18 }, this.scene
    );
    backing.position = new Vector3(
      pos.x - Math.sin(yaw) * 0.12,
      2.05,
      pos.z - Math.cos(yaw) * 0.12
    );
    backing.rotation.y = yaw;
    backing.material = getSharedMaterials(this.scene).corridorWall;
    backing.parent = this.root;
    backing.checkCollisions = true;
    wall.placeAt({
      position: pos,
      rotationY: Math.atan2(-pos.x, -pos.z),
      parent: this.root,
    });
    wall.setContent({ title: "GIVING WALL" });
    wall.onPick(() => {
      window.open("https://berich.org", "_blank", "noopener,noreferrer");
    });
    this.givingWall = wall;
  }

  private renderGivingWall(): void {
    const s: MallStats | null = this.stats;
    if (s === null || this.givingWall === undefined) {
      return;
    }
    const lines: string[] = [];
    if (s.donations !== null) {
      lines.push(`$${s.donations.totalDonated.toFixed(2)} donated`);
      lines.push(`${s.donations.totalItems} items given`);
    }
    if (s.charities.length > 0) {
      lines.push("Supporting:");
      for (const charity of s.charities.slice(0, 4)) {
        lines.push(charity.name);
      }
    }
    if (lines.length === 0) {
      return;
    }
    this.givingWall.setContent({ title: "GIVING WALL", bodyLines: lines });
  }
}
